/** The pure model of the Inspector tab (1.J deliverables 17 and 18). */
import type { LinkMetaSchemaV1, LinkTypeV1, LinkUpdateV1, LinkV1 } from '@codex/contracts';
import { relationLabel } from '../backlinks/backlinks-model.js';

export type MetaFieldKind = 'string' | 'integer' | 'number' | 'boolean' | 'enum';

export interface MetaField {
  readonly key: string;
  readonly kind: MetaFieldKind;
  readonly required: boolean;
  readonly minimum?: number;
  readonly maximum?: number;
  readonly options?: readonly string[];
}

const SIMPLE_KINDS = ['string', 'integer', 'number', 'boolean'] as const;

function numberOf(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

/**
 * One input per key of a type's `metaSchema`: `string`, `integer`, `number`, `boolean` and `enum`
 * with their bounds. `null` when any key has another schema: the meta is then shown read-only.
 */
export function metaFields(schema: LinkMetaSchemaV1): MetaField[] | null {
  const fields: MetaField[] = [];
  for (const [key, raw] of Object.entries(schema.properties)) {
    const required = schema.required.includes(key);
    const options = raw.enum;
    if (Array.isArray(options)) {
      if (!options.every((o): o is string => typeof o === 'string')) return null;
      fields.push({ key, kind: 'enum', required, options });
      continue;
    }
    const kind = SIMPLE_KINDS.find((k) => k === raw.type);
    if (kind === undefined) return null;
    const minimum = numberOf(raw.minimum);
    const maximum = numberOf(raw.maximum);
    fields.push({
      key,
      kind,
      required,
      ...(minimum === undefined ? {} : { minimum }),
      ...(maximum === undefined ? {} : { maximum }),
    });
  }
  return fields;
}

/** What an input holds: text for every kind but `boolean`. */
export type MetaInput = string | boolean;

/** The inputs' starting values from a link's `meta`. */
export function metaInputs(
  fields: readonly MetaField[],
  meta: Readonly<Record<string, unknown>>,
): Record<string, MetaInput> {
  const inputs: Record<string, MetaInput> = {};
  for (const field of fields) {
    const value = meta[field.key];
    if (field.kind === 'boolean') inputs[field.key] = value === true;
    else inputs[field.key] = value === undefined || value === null ? '' : String(value);
  }
  return inputs;
}

function inputValue(field: MetaField, input: MetaInput): unknown {
  if (field.kind === 'boolean') return input === true;
  const text = String(input).trim();
  if (text === '') return undefined;
  if (field.kind === 'integer') return /^-?\d+$/.test(text) ? Number.parseInt(text, 10) : text;
  if (field.kind === 'number') {
    const n = Number(text);
    return Number.isFinite(n) ? n : text;
  }
  return text;
}

/** The whole `meta` to send when an input differs from the link's, else `undefined`. */
export function metaPatch(
  fields: readonly MetaField[],
  current: Readonly<Record<string, unknown>>,
  inputs: Readonly<Record<string, MetaInput>>,
): Record<string, unknown> | undefined {
  const next: Record<string, unknown> = { ...current };
  let changed = false;
  for (const field of fields) {
    const input = inputs[field.key];
    if (input === undefined) continue;
    const value = inputValue(field, input);
    if (value === current[field.key]) continue;
    if (value === undefined && current[field.key] === undefined) continue;
    changed = true;
    if (value === undefined) delete next[field.key];
    else next[field.key] = value;
  }
  return changed ? next : undefined;
}

export interface TypedFields {
  readonly label: string;
  readonly role: string;
  readonly note: string;
}

/** A `links.update` holding only what differs from the link (trimmed; empty is null), or null. */
export function changedPatch(
  link: Pick<LinkV1, 'label' | 'role' | 'note' | 'meta'>,
  typed: TypedFields,
  fields: readonly MetaField[] | null,
  inputs: Readonly<Record<string, MetaInput>>,
): Omit<LinkUpdateV1, 'baseRv'> | null {
  const patch: {
    label?: string | null;
    role?: string | null;
    note?: string | null;
    meta?: Record<string, unknown>;
  } = {};
  for (const key of ['label', 'role', 'note'] as const) {
    const next = typed[key].trim() || null;
    if (next !== link[key]) patch[key] = next;
  }
  if (fields !== null) {
    const meta = metaPatch(fields, link.meta, inputs);
    if (meta !== undefined) patch.meta = meta as Record<string, never>;
  }
  return Object.keys(patch).length === 0 ? null : (patch as Omit<LinkUpdateV1, 'baseRv'>);
}

export interface InspectorItem {
  readonly link: LinkV1;
  readonly otherId: string;
  readonly direction: 'out' | 'in';
}

export interface InspectorGroup {
  readonly label: string;
  readonly items: readonly InspectorItem[];
}

/** This page's links grouped: outgoing under the type's label, incoming under its inverse label. */
export function groupLinks(
  links: readonly LinkV1[],
  types: readonly LinkTypeV1[],
  docId: string,
): InspectorGroup[] {
  const byId = new Map(types.map((t) => [t.id, t] as const));
  const groups = new Map<string, InspectorItem[]>();
  for (const link of links) {
    const direction = link.sourceId === docId ? 'out' : 'in';
    const label = relationLabel(link, byId.get(link.linkTypeId), docId);
    const list = groups.get(label) ?? [];
    list.push({ link, otherId: direction === 'out' ? link.targetId : link.sourceId, direction });
    groups.set(label, list);
  }
  return [...groups].map(([label, items]) => ({ label, items }));
}

export interface HomeControl {
  readonly label: string;
  readonly isPrimary: boolean;
}

/** The "Home" control of a selected structural link, phrased by direction; null otherwise. */
export function homeControl(
  link: Pick<LinkV1, 'sourceId' | 'isPrimary'>,
  type: Pick<LinkTypeV1, 'structural'> | undefined,
  docId: string,
  otherTitle: string,
): HomeControl | null {
  if (!type?.structural) return null;
  const outgoing = link.sourceId === docId;
  const next = !link.isPrimary;
  if (outgoing) {
    return {
      label: next ? `Make this page ${otherTitle}'s home` : `Clear ${otherTitle}'s home`,
      isPrimary: next,
    };
  }
  return {
    label: next ? `Make ${otherTitle} this page's home` : "Clear this page's home",
    isPrimary: next,
  };
}

/** The live link types an "Add link" offers: archived left out, built-ins first. */
export function offeredTypes(types: readonly LinkTypeV1[]): LinkTypeV1[] {
  return types.filter((t) => !t.archived).sort((a, b) => Number(b.builtin) - Number(a.builtin));
}
